import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  Item,
  PackageCriteria,
  PackagePagginationResponse,
  PackageSearchCriteria,
  PackageRequest,
  PackageBookingList,
  StayRequest,
  PlayRequest,
} from 'src/data/package-data';
import { RemoteHttpService } from 'src/remote-http';
import { ContentType, RemoteRequest } from 'src/remote-request';
import * as global from '../../globals';
import { Custom_RequestMethod } from '../common-service';
import {
  StayAndPlayPackage,
  StayAndPlayPackagePage,
  StayAndPlayRoundSpec,
} from 'src/data/mygolf.data';
import { JsonService } from 'src/json-util';

@Injectable({
  providedIn: 'root',
})
export class PackageService {
  constructor(
    public http: RemoteHttpService,
    private requestMethod: Custom_RequestMethod
  ) {}

  // Get List of Packages
  public getPackages(
    PackageSearchCriteria: PackageSearchCriteria
  ): Observable<StayAndPlayPackagePage> {
    let request = new RemoteRequest(
      global.ServerUrls.stayAndPlayPackage.getPackages,
      this.requestMethod.get,
      ContentType.URL_ENCODED_FORM_DATA,
      {
        pageNo: PackageSearchCriteria.pageNo,
        pageSize: PackageSearchCriteria.pageSize,
        search: PackageSearchCriteria.search,
        activeOrInactive: 'A',
        clubId: PackageSearchCriteria.clubId,
      }
    );
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  // Get Package list with pagination and based on package criteria
  public getdynamicPackages(
    PackageSearchCriteria: PackageCriteria
  ): Observable<PackagePagginationResponse> {
    let request = new RemoteRequest(
      global.ServerUrls.stayAndPlayPackage.getPackages,
      this.requestMethod.get,
      ContentType.URL_ENCODED_FORM_DATA,
      {
        pageNo: PackageSearchCriteria.pageNo,
        startDate: PackageSearchCriteria.startDate,
        // endDate: PackageSearchCriteria.endDate,
        search: PackageSearchCriteria.search,
        partnerId: PackageSearchCriteria.partnerId,
        clubId: PackageSearchCriteria.clubId,
        country: PackageSearchCriteria.country,
        state: PackageSearchCriteria.state,
        city: PackageSearchCriteria.city,
        sharing: PackageSearchCriteria.sharing,
        activeOrInactive: PackageSearchCriteria.activeOrInactive,
      }
    );
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          resp?.items?.forEach(packageDetails => {
            packageDetails?.packageStayOptions?.forEach(stayOpt => {
              JsonService.deriveFulImageURL(
                stayOpt?.hotel?.address?.countryData,
                'flagUrl',
                'assets/img/flag/default_worldwide.png'
              );
            });
            packageDetails?.packagePlayOptions?.forEach(playOpt => {
              JsonService.deriveFulImageURL(
                playOpt?.club?.address?.countryData,
                'flagUrl',
                'assets/image/default_club.png'
              );
            });
          });
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  // Get Package details by PackageId
  public getPackageById(id: number): Observable<Item & StayAndPlayPackage> {
    let request = new RemoteRequest(
      global.ServerUrls.stayAndPlayPackage.getPackages + '/' + id,
      this.requestMethod.get
    );
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          let snpPackage: StayAndPlayPackage = resp;
          console.debug('get package details ', snpPackage, resp);
          if (snpPackage) this.setPackageNumberRounds(snpPackage?.golfRoundSpec);
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  // Request from package booking
  public requestPackage(payload, packageId): Observable<Item> {
    let _url = global.ServerUrls.stayAndPlayPackage.requestPackage;
    _url = _url.replace(':packageId', packageId);
    let request = new RemoteRequest(_url, this.requestMethod.post, ContentType.JSON, {
      ...payload,
    });
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  public deleteRequestedRoom(roomRequestIds, requestId): Observable<Item> {
    let _url = global.ServerUrls.stayAndPlayPackage.deleteRequestedRoom;
    _url = _url.replace(':requestId', requestId);
    let request = new RemoteRequest(
      _url + '?roomRequestIds=' + roomRequestIds,
      this.requestMethod.delete,
      ContentType.URL_ENCODED_FORM_DATA
    );
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  public addRequestedRoom(payload, requestId): Observable<any> {
    let _url = global.ServerUrls.stayAndPlayPackage.addRequestedRoom;
    _url = _url.replace(':requestId', requestId);
    let request = new RemoteRequest(_url, this.requestMethod.put, ContentType.JSON, payload);
    return this.http
      .execute(request)
      .pipe(map(resp => resp))
      .pipe(catchError(this.http.handleError));
  }

  public updateRoomRequests(payload, requestId): Observable<any> {
    let _url = global.ServerUrls.stayAndPlayPackage.updateRoomRequests;
    _url = _url.replace(':requestId', requestId);
    let request = new RemoteRequest(_url, this.requestMethod.put, ContentType.JSON, payload);
    return this.http
      .execute(request)
      .pipe(map(resp => resp))
      .pipe(catchError(this.http.handleError));
  }

  public updateGameRequests(payload, requestId): Observable<any> {
    let _url = global.ServerUrls.stayAndPlayPackage.updateGameRequests;
    _url = _url.replace(':requestId', requestId);
    let request = new RemoteRequest(_url, this.requestMethod.put, ContentType.JSON, payload);
    return this.http
      .execute(request)
      .pipe(map(resp => resp))
      .pipe(catchError(this.http.handleError));
  }

  public updateRequest(payload): Observable<any> {
    let _url = global.ServerUrls.stayAndPlayPackage.updateRequest;
    _url = _url.replace(':requestId', payload?.requestId);
    let form = new FormData();
    form.append('requestId', payload.requestId);
    form.append('startDate', payload.startDate);
    form.append('endDate', payload.endDate);
    form.append('playerId', payload.playerId);
    form.append('requesterName', payload.requesterName);
    form.append('requesterEmail', payload.requesterEmail);
    form.append('specialInstructions', payload.specialInstructions);
    let request = new RemoteRequest(_url, this.requestMethod.put, ContentType.MULTIPART_FORM, form);
    return this.http
      .execute(request)
      .pipe(map(resp => resp))
      .pipe(catchError(this.http.handleError));
  }

  public deleteRequestedGame(gameRequestIds, requestId): Observable<any> {
    let _url = global.ServerUrls.stayAndPlayPackage.deleteRequestedGame;
    _url = _url.replace(':requestId', requestId);
    let request = new RemoteRequest(
      _url + '?gameRequestIds=' + gameRequestIds,
      this.requestMethod.delete,
      ContentType.URL_ENCODED_FORM_DATA
    );
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  public addRequestedGame(payload, requestId): Observable<any> {
    let _url = global.ServerUrls.stayAndPlayPackage.addRequestedGame;
    _url = _url.replace(':requestId', requestId);
    let request = new RemoteRequest(_url, this.requestMethod.put, ContentType.JSON, payload);
    return this.http
      .execute(request)
      .pipe(map(resp => resp))
      .pipe(catchError(this.http.handleError));
  }

  // get estimated charge
  public getEstimatedCharge(payload, packageId): Observable<Item> {
    let _url = global.ServerUrls.stayAndPlayPackage.estimatedCharge;
    _url = _url.replace(':packageId', packageId);
    let request = new RemoteRequest(_url, this.requestMethod.post, ContentType.JSON, payload);
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  // Update Request from package booking
  public updateRequestPackage(packageRequest: PackageRequest, requestId: number): Observable<Item> {
    let _url = global.ServerUrls.stayAndPlayPackage.requestPackage;
    _url = _url.replace(':packageId', packageRequest.packageId);
    let request = new RemoteRequest(
      _url + '/' + requestId,
      this.requestMethod.post,
      ContentType.URL_ENCODED_FORM_DATA,
      {
        playerId: packageRequest.playerId,
        requesterName: packageRequest.requesterName,
        requesterEmail: packageRequest.requesterEmail,
        phoneNumber: packageRequest.phoneNumber,
        noOfPax: packageRequest.noOfPax,
        preferredDates: packageRequest.preferredDates,
      }
    );
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  // Generate Request for package that will send notification to club owner
  public generateRequest(packageId: number, requestId: number) {
    let _url = global.ServerUrls.stayAndPlay.generateRequest;
    _url = _url.replace(':packageId', packageId);
    _url = _url.replace(':requestId', requestId);
    let request = new RemoteRequest(_url, this.requestMethod.post);
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  // Delete request for particular package
  public deleteRequest(packageId: number, requestId: number) {
    let _url = global.ServerUrls.stayAndPlay.stayAndPlayRequest;
    _url = _url.replace(':packageId', packageId);
    _url = _url.replace(':requestId', requestId);
    let request = new RemoteRequest(_url, this.requestMethod.delete);
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  // Get Package List based on criteria
  public getPackageBookingList(PackageListRequest: PackageBookingList): Observable<Item[]> {
    let request = new RemoteRequest(
      global.ServerUrls.stayAndPlay.getBookedPackages,
      this.requestMethod.get,
      ContentType.URL_ENCODED_FORM_DATA,
      {
        playerId: PackageListRequest.playerId,
        email: PackageListRequest.email,
        pageSize: 30,
        pageNo: 1,
        startDate: PackageListRequest.startDate,
        endDate: PackageListRequest.endDate,
        search: PackageListRequest.search,
      }
    );
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  // Request from stay options
  public requestStayOption(stayRequest: StayRequest): Observable<Item> {
    let _url = global.ServerUrls.stayAndPlay.requestPackageStayOptions;
    _url = _url.replace(':packageId', stayRequest.packageId);
    _url = _url.replace(':requestId', stayRequest.requestId);

    _url =
      _url +
      '?stayOptionId=' +
      stayRequest.stayOptionId +
      '&noOfNights=' +
      stayRequest.noOfNights +
      '&priority=' +
      stayRequest.priority;
    let request = new RemoteRequest(_url, this.requestMethod.put);
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  // public getPackage(stayRequest: StayRequest): Observable<Item> {

  //   let _url = global.ServerUrls.stayAndPlay.requestPackageStayOptions;
  //   _url = _url.replace(':packageId', stayRequest.packageId);
  //   _url = _url.replace(':requestId', stayRequest.requestId);
  //   let request = new RemoteRequest(_url,
  //     this.requestMethod.put, ContentType.URL_ENCODED_FORM_DATA, {
  //     stayOptionId: stayRequest.stayOptionId,
  //     noOfNights: stayRequest.noOfNights,
  //     priority: stayRequest.priority,
  //   }); return this.http.execute(request)
  //     .pipe(map((resp: any) => {
  //
  //       return resp;
  //     })).pipe(catchError(this.http.handleError));
  // }

  public getPackageRequestData(id: number): Observable<Item[]> {
    let _url = global.ServerUrls.stayAndPlay.getPackageRequests + '/' + id;
    let request = new RemoteRequest(_url, this.requestMethod.get);
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  // Request for Play Option
  public requestPlayOption(playRequest: PlayRequest): Observable<Item> {
    let _url = global.ServerUrls.stayAndPlay.requestPackagePlayOptions;
    _url = _url.replace(':packageId', playRequest.packageId);
    _url = _url.replace(':requestId', playRequest.requestId);

    _url =
      _url +
      '?playOptionId=' +
      playRequest.playOptionId +
      '&noOfRounds=' +
      playRequest.noOfRounds +
      '&priority=' +
      playRequest.priority;
    // let params = new HttpParams()
    // .set("playOptionId", playRequest.playOptionId)
    // .set("noOfRounds", playRequest.noOfRounds)
    // .set("priority", playRequest.priority);

    let request = new RemoteRequest(_url, this.requestMethod.put);
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  // Delete packge playOptions sequence
  public deletePlayOption(playRequest: any): Observable<Item> {
    let _url = global.ServerUrls.stayAndPlay.requestPackagePlayOptions;
    _url = _url.replace(':packageId', playRequest.packageId);
    _url = _url.replace(':requestId', playRequest.requestId);
    _url = _url + '/' + playRequest.sequenceId;
    // let params = new HttpParams()
    // .set("playOptionId", playRequest.playOptionId)
    // .set("noOfRounds", playRequest.noOfRounds)
    // .set("priority", playRequest.priority);

    let request = new RemoteRequest(_url, this.requestMethod.delete);
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  // Delete packge stayOptions sequence
  public deleteStayOption(stayRequest: any): Observable<Item> {
    let _url = global.ServerUrls.stayAndPlay.requestPackageStayOptions;
    _url = _url.replace(':packageId', stayRequest.packageId);
    _url = _url.replace(':requestId', stayRequest.requestId);

    _url = _url + '/' + stayRequest.sequenceId;
    let request = new RemoteRequest(_url, this.requestMethod.delete);
    return this.http
      .execute(request)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      )
      .pipe(catchError(this.http.handleError));
  }

  noOfRounds = new BehaviorSubject<StayAndPlayRoundSpec>({
    eighteenHoleRounds: 0,
    nineHoleRounds: 0,
  });
  clearPackageNumberRounds() {
    this.noOfRounds.next({
      eighteenHoleRounds: 0,
      nineHoleRounds: 0,
    });
  }
  setPackageNumberRounds(newValue) {
    this.noOfRounds.next(newValue);
  }
  getPackageNumberRounds() {
    return this.noOfRounds.asObservable();
  }
  getMaxPackageNumberRounds() {
    let totalRounds = 0;
    if(this.noOfRounds)
    this.noOfRounds.subscribe((a: StayAndPlayRoundSpec) => {
      // console.debug("get max package no of rounds : ", a, typeof a);
      if(a !== undefined && a !== NaN) {
        totalRounds += a?.eighteenHoleRounds;
        totalRounds += a?.nineHoleRounds;
      }
    });

    console.debug("get allowed max rounds", this.noOfRounds, totalRounds);
    // console.debug("get max package no of rounds [total]: ", totalRounds);
    return totalRounds;
  }
  requestRounds = new BehaviorSubject<number>(0);
  setRequestRounds(newValue) {
    this.requestRounds.next(newValue);
  }
  getRequestRounds() {
    return this.requestRounds.asObservable();
  }

  requestGuests = new BehaviorSubject<number>(0);

  setRequestGuests(newValue) {
    this.requestGuests.next(newValue);
  }
  getRequestGuests() {
    return this.requestGuests.asObservable();
  }
}
